/**
 * Reusable plain-English explainers for the salience pages. One home for the
 * copy so the overview and term-detail pages stay consistent and DRY. Each
 * block is a short lead written for people who don't know the academic work,
 * with the paper reference kept as a muted <Cite> footnote rather than the
 * headline. The fuller, expansive version of all this lives on the methods page.
 *
 * House style: NO em-dashes in any visible copy (user preference).
 */
import { Link } from "react-router-dom";
import { InfoCallout, Cite } from "./InfoCallout";

type Tone = "dark" | "light";

/** Small "read the full method" link, tone-aware. */
export function MethodsLink({ tone = "dark" }: { tone?: Tone }) {
  const cls =
    tone === "light"
      ? "text-brand-indigo hover:underline"
      : "text-[#7da6ff] hover:underline";
  return (
    <Link to="/salience/methods" className={`text-[11px] font-medium ${cls}`}>
      Full method and sources →
    </Link>
  );
}

/** The whole concept, in one toggle. */
export function WhatIsThisCallout({ tone = "light" }: { tone?: Tone }) {
  return (
    <InfoCallout label="What am I looking at?" tone={tone}>
      <p>
        This tracks how much attention the idea of a wealth tax is getting, and
        how far Gary's way of talking about it travels. We watch four separate
        conversations: what creators post (YouTube), what people search for
        (Google), the news media, and Parliament. We keep them apart on purpose,
        because a topic can be loud in the news yet quiet in Parliament, and that
        gap is part of the story.
      </p>
      <MethodsLink tone={tone} />
    </InfoCallout>
  );
}

/** Prominence is not the same as importance. */
export function AttentionNotImportanceCallout({ tone = "light" }: { tone?: Tone }) {
  return (
    <InfoCallout label="Why we say “attention”, not “importance”" tone={tone}>
      <p>
        We measure how prominent the topic is, meaning how often it shows up.
        That is not the same as how much people actually care about it. Treating
        the two as one is a well-known trap, so we only ever claim to track
        prominence over time.
      </p>
      <Cite tone={tone}>
        Wlezien (2005), on separating an issue's importance from its current
        problem-status.
      </Cite>
    </InfoCallout>
  );
}

/** The four agendas, what each pulls from, and why we never blend them. */
export function FourAgendasCallout({ tone = "light" }: { tone?: Tone }) {
  return (
    <InfoCallout label="The four conversations we track" tone={tone}>
      <ul className="space-y-1.5">
        <li>
          <span className="font-semibold">Public:</span> how many YouTube videos
          creators post about the topic, counted per day, with Gary's own uploads
          kept separate.
        </li>
        <li>
          <span className="font-semibold">Search:</span> how much people look the
          topic up on Google, using Google Trends. Shown as a 0 to 100 index (100
          is that term's own busiest point), not a percentage.
        </li>
        <li>
          <span className="font-semibold">Media:</span> how much of the world's
          news mentions it. This comes from GDELT, an open global news index, and
          is measured as a share of all coverage rather than raw volume.
        </li>
        <li>
          <span className="font-semibold">Elite:</span> how often it comes up in
          UK Parliament and government (Hansard debates, Early Day Motions,
          Written Questions, petitions, and GOV.UK).
        </li>
      </ul>
      <p>
        We never add these into a single score. Blending would hide the
        interesting part: where they agree, and where they pull apart.
      </p>
      <Cite tone={tone}>
        Rogers &amp; Dearing (1988) and McCombs &amp; Shaw (1972), on
        agenda-setting across separate public, media, and policy agendas.
      </Cite>
      <MethodsLink tone={tone} />
    </InfoCallout>
  );
}

/** What's actually on the watchlist, the co-occurrence nuance, and that it's provisional. */
export function TrackedTermsCallout({ tone = "light" }: { tone?: Tone }) {
  return (
    <InfoCallout label="What's on the watchlist?" tone={tone}>
      <p>
        We track more than the words “wealth tax”. The watchlist covers a few
        related topics (wealth tax, tax the rich, billionaire tax), Gary's
        tagline, and around two dozen people and organisations in the movement:
        allied economists (such as Piketty, Zucman, and Richard Murphy),
        advocacy groups (such as Patriotic Millionaires and Tax Justice UK), and
        adjacent creators and politicians.
      </p>
      <p>
        Most of those people and groups are counted only when they come up{" "}
        <em>alongside</em> the wealth-tax conversation, so it is a loose
        “discussed in this context” signal, not a measure of their overall fame.
        Gary himself, his book, and the main advocacy groups are tracked in full.
      </p>
      <p>
        This list was picked from early desk research plus the audience's own
        collaboration suggestions. It is a starting point, not a settled set.
        Getting the most useful mix would take more input from Gary and the team.
      </p>
      <MethodsLink tone={tone} />
    </InfoCallout>
  );
}

/** Small inline note for a topic-scoped entity/tagline term page. */
export function CoOccurrenceNote({ tone = "dark" }: { tone?: Tone }) {
  const cls = tone === "light" ? "text-ink-muted" : "text-terminal-ink-muted";
  return (
    <p className={`text-[11px] leading-snug ${cls}`}>
      Measured only when this term appears alongside the wealth-tax conversation,
      so it reflects how often it comes up in that context, not its overall
      profile.
    </p>
  );
}

/** Stance is a position on the proposal, not positive/negative language. */
export function StanceCallout({ tone = "dark" }: { tone?: Tone }) {
  return (
    <InfoCallout label="How this works" tone={tone}>
      <p>
        Stance is whether a piece of text argues for or against a wealth tax,
        that is, its position on the proposal. That is different from sentiment,
        which is just whether the language sounds positive or negative. An angry
        comment can still support the policy, and a polite one can oppose it.
      </p>
      <Cite tone={tone}>
        Bestvater &amp; Monroe (2023), showing stance and sentiment are not
        interchangeable.
      </Cite>
    </InfoCallout>
  );
}

/** A frame is which angle the argument takes; auto-tagging is only a rough steer. */
export function FrameCallout({ tone = "dark" }: { tone?: Tone }) {
  return (
    <InfoCallout label="How this works" tone={tone}>
      <p>
        A frame is the angle an argument takes on the same topic: fairness, the
        risk of wealth leaving the country, whether it is workable in practice,
        or “tax the billionaires”. It is a separate question from whether the
        text supports or opposes the tax.
      </p>
      <p>
        Worth knowing: our automatic frame tagging only agrees with human coders
        about six times in ten, so read this breakdown as a rough steer, not a
        precise figure.
      </p>
      <Cite tone={tone}>
        Entman (1993), on framing as competing problem-definitions. Reliability
        from our own coder eval (Krippendorff's alpha ≈ 0.59).
      </Cite>
    </InfoCallout>
  );
}

/** Lead-lag findings are candidates, never proof of cause. */
export function InfluenceCallout({ tone = "dark" }: { tone?: Tone }) {
  return (
    <InfoCallout label="How this works" tone={tone}>
      <p>
        This looks for cases where attention rises in one conversation shortly
        before it rises in another, a hint that one might be leading the other.
        It is a statistical “came before”, not proof of cause, so we only ever
        call these candidates.
      </p>
      <p>
        Every candidate is checked against a calendar of big events (Budgets,
        elections, major reports) that could explain both rises at once.
        “Granger p” is the confidence figure: lower means the lead-then-follow
        pattern is less likely to be a fluke.
      </p>
      <Cite tone={tone}>
        Granger (2004), on predictive precedence rather than causation. Event
        checks follow Kingdon's idea of policy windows.
      </Cite>
      <MethodsLink tone={tone} />
    </InfoCallout>
  );
}

/** Geo tags are indicative; read the balance, not the exact figures. */
export function GeoCallout({ tone = "dark" }: { tone?: Tone }) {
  return (
    <InfoCallout label="How this works" tone={tone}>
      <p>
        This shows where coverage is landing, the UK against the rest of the
        world. The location tags from our news source are approximate, so read
        the UK-versus-global balance rather than the exact numbers.
      </p>
      <Cite tone={tone}>
        GDELT country tagging, treated as indicative only.
      </Cite>
    </InfoCallout>
  );
}

/** Tone is weak and reported quietly. */
export function ToneCallout({ tone = "dark" }: { tone?: Tone }) {
  return (
    <InfoCallout label="About the tone figure" tone={tone}>
      <p>
        Tone is a rough reading of whether the language sounds positive or
        negative. Automatic tone is unreliable, so we show it quietly and would
        not lean on it.
      </p>
      <Cite tone={tone}>
        González-Bailón &amp; Paltoglou (2015), on automated tone methods
        disagreeing.
      </Cite>
    </InfoCallout>
  );
}

/**
 * A calm, honest prototype note. `variant="inline"` is a one-liner for the top
 * of a page; `variant="full"` is a short paragraph for the methods page.
 */
export function PrototypeNote({
  tone = "light",
  variant = "inline",
  evalLink = false,
}: {
  tone?: Tone;
  variant?: "inline" | "full";
  evalLink?: boolean;
}) {
  const base =
    tone === "light" ? "text-ink-muted" : "text-terminal-ink-muted";
  const linkCls =
    tone === "light"
      ? "text-brand-indigo hover:underline"
      : "text-[#7da6ff] hover:underline";

  if (variant === "inline") {
    return (
      <p className={`text-[11px] leading-relaxed ${base}`}>
        <span className="font-semibold">Early prototype.</span> The data is
        gathered automatically and only lightly checked, so expect gaps and the
        odd wrong call. Read it for the shape of a trend, not as a precise figure.
      </p>
    );
  }

  return (
    <div className={`text-sm leading-relaxed ${base}`}>
      <p>
        This is an early prototype, built to show what the approach can do. The
        data is gathered automatically from public sources and has had only light
        validation so far, so expect gaps, some noise, and the occasional wrong
        call. Treat everything here as directional: read the shape and direction
        of a trend rather than any single precise number.
      </p>
      {evalLink && (
        <p className="mt-2">
          The one part we do measure formally is how well our automatic coder
          agrees with a human.{" "}
          <Link to="/salience/eval" className={`font-medium ${linkCls}`}>
            See the coder reliability check →
          </Link>
        </p>
      )}
    </div>
  );
}
